import type { GeoItem } from '@/hooks/useSuperStats';

interface GeoBreakdownProps {
  countries: GeoItem[];
  cities: GeoItem[];
  total: number;
  pending: number;
  loading: boolean;
}

/** Convertit un code pays ISO (MA, FR…) en drapeau emoji. */
function flagOf(code: string | null): string {
  if (!code || code.trim().length !== 2) return 'ri-earth-line';
  const base = 0x1f1e6;
  const upper = code.trim().toUpperCase();
  const first = upper.charCodeAt(0) - 65;
  const second = upper.charCodeAt(1) - 65;
  if (first < 0 || first > 25 || second < 0 || second > 25) return 'ri-earth-line';
  return String.fromCodePoint(base + first, base + second);
}

function isFlag(value: string): boolean {
  return value.startsWith('\u{1F1E6}') || value.length === 2;
}

/**
 * Répartition géographique réelle des visiteurs : pays (avec drapeau) puis
 * principales villes, à partir de la géolocalisation IP des visites publiques.
 */
export default function GeoBreakdown({
  countries,
  cities,
  total,
  pending,
  loading,
}: GeoBreakdownProps) {
  const max = countries[0]?.value || 1;

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 h-full flex flex-col">
      <div className="flex items-center justify-between gap-2 mb-1">
        <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
          <i className="ri-map-2-line text-primary-500"></i>
          Répartition géographique
        </h3>
        {total > 0 && (
          <span className="text-[11px] text-foreground-400 whitespace-nowrap">
            {total.toLocaleString('fr-FR')} visite(s) géolocalisée(s)
          </span>
        )}
      </div>
      <p className="text-[11px] text-foreground-400 mb-4">
        D&apos;après l&apos;adresse IP des visiteurs (visites publiques).
      </p>

      {loading ? (
        <div className="flex items-center justify-center h-40 flex-1">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : countries.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center text-foreground-400 py-6">
          <i className="ri-map-pin-2-line text-3xl mb-2"></i>
          <p className="text-sm">Aucune donnée géographique sur cette période</p>
          <p className="text-xs mt-1 max-w-[240px]">
            {pending > 0
              ? `${pending} visite(s) en attente de géolocalisation — elles apparaîtront après l'enrichissement automatique.`
              : 'Les prochaines visites publiques seront géolocalisées automatiquement.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4 flex-1">
          <div className="space-y-2.5">
            {countries.map((country) => {
              const flag = flagOf(country.code);
              return (
                <div key={country.name} className="flex items-center gap-3">
                  <span className="w-7 h-7 rounded-md bg-background-100 flex items-center justify-center flex-shrink-0 text-sm">
                    {isFlag(flag) ? flag : <i className="ri-earth-line text-foreground-400"></i>}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-sm text-foreground-900 truncate">{country.name}</span>
                      <span className="text-xs font-bold text-primary-700 bg-primary-100 rounded-full px-2 py-0.5 whitespace-nowrap">
                        {country.value.toLocaleString('fr-FR')}
                      </span>
                    </div>
                    <div className="h-1.5 bg-background-200/70 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary-400 rounded-full"
                        style={{ width: `${(country.value / max) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {cities.length > 0 && (
            <div className="pt-3 border-t border-background-200/60">
              <div className="text-[11px] font-semibold text-foreground-500 mb-2 flex items-center gap-1.5">
                <i className="ri-building-2-line"></i>
                Principales villes
              </div>
              <div className="flex flex-wrap gap-1.5">
                {cities.map((city) => (
                  <span
                    key={city.name}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-[11px]"
                  >
                    {city.name}
                    <span className="font-bold">{city.value.toLocaleString('fr-FR')}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}